<template>
  <li :class="{ 'last-action': isLastAction }">
    <div class="row">
      <div class="col-12 col-lg-7">
        <label class="sr-only">Action step</label>
        <quill-wrapper :config="quillConfig" @change="setDescription($event)" />
      </div>
      <div class="col-6 col-lg-2">
        <label class="sr-only">Action step start date</label>

        <flatpickr
          class="form-control"
          name="start_date"
          :config="dateConfig"
          v-model="stateStartDate"
          :class="[validationClasses('start_date', scope)]"
          :data-vv-scope="scope"
          placeholder="Add date"
          v-validate.disable="{ required: !isLastAction }"
        />
      </div>
      <div class="col-6 col-lg-2">
        <label class="sr-only">Action step end date</label>

        <flatpickr
          class="form-control"
          name="end_date"
          :data-vv-scope="scope"
          :config="endDateConfig"
          :class="[validationClasses('end_date', scope)]"
          v-model="stateEndDate"
          placeholder="Add date"
          v-validate.disable="{ required: !isLastAction }"
        />
      </div>
      <div class="col-auto d-flex align-items-center">
        <button
          type="button"
          class="btn btn-link text-danger btn-delete-action-step"
          @click="confirmBeforeDeleting"
        >
          <font-awesome :icon="['far', 'trash-alt']"/>
          <span class="sr-only">Delete Action Steps</span>
        </button>
      </div>
    </div><!-- /.row -->
  </li>
</template>

<script>
import { alert } from 'utils'
import { placementDevelopmentObjectiveMethods, placementComputed } from 'vx/helpers'
import Flatpickr from 'vue-flatpickr-component'
import moment from 'moment'

export default {
  inject: ['$validator'],

  props: {
    actionIndex: Number,
    objectiveIndex: Number,
    isLastAction: Boolean,
    action: {
      required: true,
      type: Object
    }
  },

  components: {
    Flatpickr
  },

  data () {
    return {
      dateConfig: {
        altInput: true,
        altFormat: 'd/m/Y',
        dateFormat: 'Z',
        disable: [(date) => {
          return moment(date).isBefore(
            moment(this.placement.start_date).add(-1, 'day')
          ) || moment(date).isAfter(
            moment(this.placement.end_date).add(1, 'day')
          )
        }]
      }
    }
  },

  watch: {
    stateStartDate (val) {
      if (moment(this.stateEndDate).isBefore(moment(val))) {
        this.stateEndDate = null
      }
    }
  },

  computed: {
    ...placementComputed,

    /**
     * https://support.codium.com.au/a/tickets/12145
     * cannot select an end date before start date
     *
     * @return object
     */
    endDateConfig () {
      return !this.stateStartDate ? this.dateConfig : Object.assign({}, this.dateConfig, {
        disable: [(date) => {
          return moment(date).isBefore(moment(this.stateStartDate)) || moment(date).isAfter(this.placement.end_date)
        }]
      })
    },

    scope () {
      return `objectives.${this.objectiveIndex}.actions.${this.actionIndex}`
    },

    quillConfig () {
      return {
        options: { placeholder: 'Add action step...' },
        value: this.stateDescription
      }
    },

    stateDescription: {
      get () {
        return this.action.description
      },

      set (value) {
        this.setDescription(value)
      }
    },

    stateStartDate: {
      get () {
        return this.action.start_date
      },

      set (value) {
        this.setActionProperty({
          actionIndex: this.actionIndex, objectiveIndex: this.objectiveIndex, property: 'start_date', value
        })
      }
    },

    stateEndDate: {
      get () {
        return this.action.end_date
      },

      set (value) {
        this.setActionProperty({
          actionIndex: this.actionIndex, objectiveIndex: this.objectiveIndex, property: 'end_date', value
        })
      }
    }
  },

  methods: {
    ...placementDevelopmentObjectiveMethods,

    setDescription (value) {
      this.setActionProperty({
        actionIndex: this.actionIndex, objectiveIndex: this.objectiveIndex, property: 'description', value
      })
    },

    async confirmBeforeDeleting () {
      if (
        !await alert.confirm({
          title: 'Are you sure?',
          text: 'Are you sure you want to delete this action step.'
        })
      ) {
        return
      }

      this.deleteAction({
        objectiveIndex: this.objectiveIndex,
        actionIndex: this.actionIndex
      })
    }
  }
}
</script>
